import { type MeResponse, isAdminRole, roleHasGmsAccess } from "@seagames/contracts";
import { groupGrants } from "../travel/access";

/**
 * Who sees what on the Accommodation pages (M11, PLAN §3.1 / §4). NONE of
 * this decides anything: every read is scoped in SQL (`cardInScope`,
 * `buildingInScope`, `accommodation-rules.ts`) and every write is re-checked
 * by the API. It only picks which page, tabs and buttons to draw.
 *
 * Who someone IS comes from the active role, never a permission (bug 7): a
 * hotel login is HOTEL whatever its group holds and gets the hotel view; an
 * ORGANIZATION login is a NOC and gets its own rooming, never the host desk.
 */

type Session = Pick<MeResponse, "active_role" | "group">;

/** A hotel's login with an active group for that role (a facility role, like TRANSPORT_PROVIDER). */
export const isHotel = (me: Session): boolean => me.active_role === "HOTEL" && roleHasGmsAccess(me.active_role, me.group);

/** A NOC: an ORGANIZATION login with its active group. Reads its own rooming (`cardInScope`), never the host pages. */
export const isNoc = (me: Session): boolean => me.active_role === "ORGANIZATION" && roleHasGmsAccess(me.active_role, me.group);

/** The host's accommodation office: buildings, rooms, reservations, stay defaults, assign / move / remove, auto-assign. */
export const canManageAccommodation = (me: Session): boolean =>
	isAdminRole(me.active_role) || (me.active_role === "OPERATION" && groupGrants(me, "accommodation.manage"));

/** The front desk: check in / out / undo, scan, arrivals and departures. A hotel's desk acts on its own buildings only. */
export const canUseDesk = (me: Session): boolean =>
	isAdminRole(me.active_role) || ((me.active_role === "OPERATION" || me.active_role === "HOTEL") && groupGrants(me, "accommodation.desk"));

/** Helpdesk (S5's tab): work tickets or report them. */
export const canSeeHelpdesk = (me: Session): boolean => isAdminRole(me.active_role) || groupGrants(me, "helpdesk.handle") || groupGrants(me, "helpdesk.report");

/**
 * The host pages (Overview, Rooming, contingents, Check-in & out): admins
 * and an OPERATION group with any `accommodation.*` permission.
 */
export const canSeeHostAccommodation = (me: Session): boolean =>
	isAdminRole(me.active_role) || (me.active_role === "OPERATION" && (groupGrants(me, "accommodation.manage") || groupGrants(me, "accommodation.desk")));

/** Buildings and floor maps: the host pages, or a hotel (its own buildings, read-only). */
export const canSeeBuildings = (me: Session): boolean => canSeeHostAccommodation(me) || isHotel(me);

/**
 * Which page a session gets at `/accommodation`:
 *   host      the Overview (admins, OPERATION with `accommodation.*`)
 *   noc       its own rooming (S5, `components/accommodation-noc`)
 *   hotel     arriving today / in house / departing (S5, `components/accommodation-hotel`)
 *   helpdesk  an OPERATION group with only `helpdesk.*`: the index sends it to the Helpdesk tab
 *   null      none — the guard sends it home
 */
export type AccommodationView = "host" | "noc" | "hotel" | "helpdesk";

export function accommodationView(me: Session): AccommodationView | null {
	if (canSeeHostAccommodation(me)) return "host";
	if (isHotel(me)) return "hotel";
	if (isNoc(me)) return "noc";
	if (canSeeHelpdesk(me)) return "helpdesk";
	return null;
}

/** The route guard and the nav: anyone with a view. */
export const canSeeAccommodation = (me: Session): boolean => accommodationView(me) !== null;
